import * as React from 'react';

/**
 * Anything whose underlying chunk can be warmed ahead of render. Kept prop-type
 * agnostic so heterogeneous screens can be grouped into a preload "bunch"
 * (see {@link getScreenNeighbors}) — the bunch only ever calls `preload()`.
 */
export interface Preloadable {
    /**
     * Kick off (and memoize) the dynamic import so the underlying chunk is
     * fetched ahead of render. Safe to call repeatedly — the import only
     * runs once.
     */
    preload: () => Promise<unknown>;
}

/**
 * A lazily-loaded component that can also be preloaded imperatively.
 *
 * `React.lazy` memoizes its factory internally but gives no way to trigger the
 * dynamic import ahead of render, so we keep our own handle on the import
 * promise to warm the chunk before the component is actually shown.
 */
export type PreloadableComponent<P extends object = Record<string, unknown>> =
    React.LazyExoticComponent<React.ComponentType<P>> & Preloadable;

/**
 * Wrap a dynamic import in `React.lazy` while also exposing a `preload()` method.
 *
 * Preloading lets us fetch a screen's chunk during idle time (see
 * {@link useNeighborPreload}) so navigating to it doesn't block on the network.
 */
export function lazyWithPreload<P extends object>(
    factory: () => Promise<{ default: React.ComponentType<P> }>
): PreloadableComponent<P> {
    const Component = React.lazy(factory) as PreloadableComponent<P>;
    let promise: Promise<unknown> | undefined;
    Component.preload = () => {
        promise ??= factory();
        return promise;
    };
    return Component;
}
